import * as React from 'react';
import * as _ from 'lodash';

import { ChoiceGroup, Dropdown, IChoiceGroupOption, MaskedTextField, PrimaryButton, TextField, IDropdownOption, Stack, ActionButton, ComboBox, IComboBoxOption, IComboBox } from '@fluentui/react';

import { AsyncData } from '../../redux/core';
import { MultiSelector } from '../core/MultiSelector';
import { PageHeader } from '../core/PageHeader';
import { ToastParams } from '../../redux/message/types';
import { isNPIValid } from '../../util/String';
import { ServiceOffering } from '../../api/services.types';
import { Market } from '../../api/markets.types';
import { createProvider } from '../../api/providers';
import { CreateProviderRequest } from '../../api/providers.types';
import { CLINICIAN_SUFFIX_OPTIONS, SUPPORT_STAFF_SUFFIX_OPTIONS } from '../../config/constants';

export interface StateProps {
  markets: AsyncData<Market[]>;
  services: AsyncData<ServiceOffering[]>;
}

export interface DispatchProps {
  actions: {
    toast: (value: ToastParams) => void;
  };
}

type ProviderCreateProps = StateProps & DispatchProps;

export interface ProviderCreateState {
  request: CreateProviderRequest;
  suffixOptions: IComboBoxOption[];
  saving: boolean;
}

export class ProviderCreate extends React.Component<ProviderCreateProps, ProviderCreateState> {
  constructor(props: ProviderCreateProps) {
    super(props);

    this.state = {
      request: { active: true, providerType: null } as any as CreateProviderRequest,
      suffixOptions: [],
      saving: false,
    };
  }

  public render(): JSX.Element {
    return (
      <div style={{ maxWidth: 800 }}>
        <PageHeader title="Create Provider" data-tid="txt_title" />
        <ChoiceGroup
          label="Type"
          selectedKey={this.state.request.providerType}
          onChange={this.onTypeChange}
          disabled={this.state.saving}
          options={[
            {
              key: 'DOCTOR',
              iconProps: { iconName: 'Medical' },
              text: 'Clinician',
            },
            {
              key: 'MEDICAL_ASSISTANT',
              iconProps: { iconName: 'Hospital' },
              text: 'Support Staff',
            },
          ]}
        />
        {this.renderFields()}
        <PrimaryButton style={{ marginTop: 20 }} onClick={this.onSubmit} disabled={!this.canSubmit()} text="Create" data-tid="btn_create" />
        {/* {this.renderDebug()} */}
      </div>
    );
  }

  private renderFields(): JSX.Element | null {
    const { request, suffixOptions, saving } = this.state;

    if (!request.providerType) {
      return null;
    }
    let prefixOptions: IDropdownOption[];
    if (request.providerType === 'DOCTOR') {
      prefixOptions = [
        { key: '', text: 'None' },
        { key: 'Dr.', text: 'Dr.' },
      ];
    } else {
      prefixOptions = [
        { key: '', text: 'None' },
      ];
    }
    return (
      <Stack tokens={{ childrenGap: 6 }}>
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <Dropdown
            label="Prefix"
            selectedKey={request.prefix}
            onChange={(e, item) => {
              const prefix = item ? item.key.toString() : '';
              this.setState({ request: { ...request, prefix: prefix } });
            }}
            placeholder="Select"
            required={request.providerType === 'DOCTOR'}
            disabled={saving}
            options={prefixOptions}
            data-tid="dd_prefix"
          />
          <TextField
            label="First name"
            required={true}
            onChange={(e, v) => this.setState({ request: { ...request, firstName: v } })}
            value={request.firstName}
            disabled={saving}
            data-tid="inp_firstName"
          />
          <TextField
            label="Last name"
            required={true}
            onChange={(e, v) => this.setState({ request: { ...request, lastName: v } })}
            value={request.lastName}
            disabled={saving}
            data-tid="inp_lastName"
          />
          <ComboBox
            label="Suffix"
            allowFreeform={true}
            autoComplete="on"
            options={suffixOptions}
            onChange={(event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string) => {
              // If the specified suffix isn't in the list, it was manually typed.
              // In this case, we need to add it to the options list.
              if (!option && value) {
                // If the user has entered LVN or LPN, replace it with the new merged type, LVN/LPN.
                if (value.toUpperCase() === 'LVN' || value.toUpperCase() === 'LPN') {
                  value = 'LVN/LPN';
                }

                // Save the match indexes of the entry in both sets of options.
                // This will allow us to replace what was typed with the properly capitalized version below.
                const matchIndexClinician = CLINICIAN_SUFFIX_OPTIONS.findIndex(option => (option.key as String).toUpperCase() === value?.toUpperCase());
                const matchIndexSupportStaff = SUPPORT_STAFF_SUFFIX_OPTIONS.findIndex(option => (option.key as String).toUpperCase() === value?.toUpperCase());

                if (request.providerType === 'DOCTOR' && matchIndexSupportStaff > -1) {
                  // If the user has typed a support staff suffix on a clinician, swap the provider type.
                  this.onTypeChange(undefined, { key: 'MEDICAL_ASSISTANT' } as IChoiceGroupOption);
                  this.setState({ request: { ...this.state.request, providerType: 'MEDICAL_ASSISTANT', prefix: '', suffix: SUPPORT_STAFF_SUFFIX_OPTIONS[matchIndexSupportStaff].key as string } });
                } else if (request.providerType === 'MEDICAL_ASSISTANT' && matchIndexClinician > -1) {
                  // If the user has typed a clinician on a support staff, swap the provider type.
                  this.onTypeChange(undefined, { key: 'DOCTOR' } as IChoiceGroupOption);
                  this.setState({ request: { ...this.state.request, providerType: 'DOCTOR', prefix: '', suffix: CLINICIAN_SUFFIX_OPTIONS[matchIndexClinician].key as string } });
                } else {
                  // Otherwise, create a new option and add it to the existing list.
                  this.setState({
                    suffixOptions: [...suffixOptions, { key: value, text: value }],
                    request: { ...this.state.request, suffix: value }
                  });
                }
              } else if (value) {
                this.setState({ request: { ...this.state.request, suffix: value } });
              }
            }}
            selectedKey={request.suffix}
            disabled={saving}
            data-tid="inp_suffix"
          />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <TextField
            label="Email"
            required={true}
            onChange={(e, v) => this.setState({ request: { ...this.state.request, username: v! } })}
            value={this.state.request.username}
            disabled={this.state.saving}
            data-tid="inp_email"
          />
          <MaskedTextField
            value={request.phone}
            onChange={(e, v) => this.setState({ request: { ...request, phone: _.trim(v) } })}
            label="Phone Number"
            required={true}
            mask="+9 (999)-999-9999"
            maskChar=""
            maxLength={17}
            disabled={saving}
            data-tid="inp_phone"
          />
          <Dropdown
            label="Sex"
            disabled={saving}
            selectedKey={request.gender}
            required={true}
            onChange={(ev, opt) => opt && this.setState({ request: { ...request, gender: opt.key as string } })}
            options={[
              {
                key: 'MALE',
                text: 'Male',
              },
              {
                key: 'FEMALE',
                text: 'Female',
              },
            ]}
            data-tid="dd_gender"
          />
        </Stack>
        {request.providerType === 'DOCTOR' && (
          <>
            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
              <TextField
                label="NPI"
                required={this.isNPIRequired()}
                onChange={(e, v) => this.setState({ request: { ...request, providerNPI: v } })}
                onGetErrorMessage={this.checkNPI}
                deferredValidationTime={600}
                value={request.providerNPI}
                disabled={saving}
                data-tid="inp_npi"
              />
              <ActionButton
                onClick={() => window.open(`https://npiregistry.cms.hhs.gov/registry/provider-view/${this.state.request.providerNPI}`, '_blank')}
                iconProps={{ iconName: 'NavigateExternalInline' }}
                allowDisabledFocus
                disabled={saving || !request.providerNPI}
              >
                Open NPI registry
              </ActionButton>
              <TextField
                label="Taxonomy Code"
                required={this.isNPIRequired()}
                onChange={(e, v) => this.setState({ request: { ...request, providerTaxonomyCode: v } })}
                value={request.providerTaxonomyCode}
                disabled={saving || !request.providerNPI}
                data-tid="inp_taxonomyCode"
              />
            </Stack>
            <MultiSelector
              label="Services"
              placeholder="Select services"
              required={true}
              disabled={saving}
              items={(this.props.services.data || []).map((s) => ({ key: s.id, text: s.name }))}
              selectedKeys={request.services}
              onChange={(v) => this.setState({ request: { ...request, services: v } })}
              data-tid="dd_services"
            />
          </>
        )}
        <MultiSelector
          label="Markets"
          placeholder="Select markets"
          disabled={saving}
          required={true}
          items={(this.props.markets.data || []).map((m) => ({ key: m.code, text: m.name }))}
          selectedKeys={request.markets}
          onChange={(v) => this.setState({ request: { ...request, markets: v } })}
        />
        <MultiSelector
          label="PD Eligible Markets"
          placeholder="Select PD Eligible markets"
          disabled={saving}
          required={false}
          items={(this.props.markets.data || []).filter((m) => (request.markets || []).indexOf(m.code as string) > -1).map((m) => ({ key: m.code, text: m.name }))}
          selectedKeys={request.pdMarkets}
          onChange={(v) => this.setState({ request: { ...request, pdMarkets: v } })}
        />
        <TextField
          label="Bio"
          onChange={(e, v) => this.setState({ request: { ...request, bio: v } })}
          value={request.bio}
          multiline={true}
          rows={4}
          disabled={saving}
          data-tid="inp_bio"
        />
        <TextField
          label="Education"
          onChange={(e, v) => this.setState({ request: { ...request, education: v } })}
          value={request.education}
          multiline={true}
          rows={4}
          disabled={saving}
          data-tid="inp_education"
        />
      </Stack>
    );
  }

  private checkNPI = (value: string) => {
    if (value && !isNPIValid(value)) {
      return ' The NPI number is not correct.';
    }
    return '';
  };

  private onTypeChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
    if (option) {
      this.setState({
        request: { ...this.state.request, providerType: option.key as any, prefix: '', suffix: '' },
      });
      
      if (option.key === 'DOCTOR') {
        this.setState({ suffixOptions: CLINICIAN_SUFFIX_OPTIONS });
      } else if (option.key === 'MEDICAL_ASSISTANT') {
        this.setState({ suffixOptions: SUPPORT_STAFF_SUFFIX_OPTIONS });
      } else {
        this.setState({ suffixOptions: [] });
      }
    }
  };

  private canSubmit(): boolean {
    const { request, saving } = this.state;
    let canSubmit =
      !!request.providerType &&
      !!request.firstName &&
      !!request.lastName &&
      !!request.username &&
      !!request.gender &&
      !!request.phone &&
      !!request.markets &&
      request.markets.length > 0;

    if (request.providerType === 'DOCTOR' && this.isNPIRequired()) {
      canSubmit =
        canSubmit &&
        !!request.providerNPI &&
        isNPIValid(request.providerNPI) &&
        !!request.providerTaxonomyCode &&
        !!request.services &&
        request.services.length > 0
    }

    return canSubmit && !saving;
  }

  private onSubmit = async () => {
    const params: any = { ...this.state.request };
    if (this.state.request.providerType === 'MEDICAL_ASSISTANT') {
      delete params.services;
      delete params.providerNPI;
      delete params.providerTaxonomyCode;
    }

    this.setState({ saving: true });
    try {
      await createProvider(params);
      this.props.actions.toast({
        type: 'success',
        title: 'Provider created',
        content: 'The provider was successfully created.',
      });
    } catch (e: any) {
      this.props.actions.toast({
        type: 'error',
        title: 'Failed to create Provider',
        content: e.message,
      });
    }

    this.setState({ saving: false });
  };

  private isNPIRequired(): boolean {
    return ['LVN', 'LPN', 'LVN/LPN', 'RN'].indexOf(this.state.request.suffix || '') === -1;
  }

  private renderDebug(): JSX.Element | null {
    return (
      <pre>
        <code>{JSON.stringify(this.state.request, null, 4)}</code>
      </pre>
    );
  }
}
